import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { hasObjectKeys, isEmptyConfig, mergeUserConfig } from "./check";
import { FeatureFlagsConfig } from "./types";

type FeatureFlagsContextValue = {
  config: FeatureFlagsConfig | null | undefined;
  setUserConfig: (config: unknown) => void;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export const FeatureFlagsProvider: React.FC<{
  config: FeatureFlagsConfig | undefined | null; // base config
  children: React.ReactNode;
  onConfigError?: (message?: string) => void;
}> = ({ config: baseConfig, onConfigError, children }) => {
  const [userConfig, setUserConfigState] = useState<unknown>(null);
  const firedRef = useRef(false);

  const mergedConfig = useMemo(() => {
    return mergeUserConfig(baseConfig, userConfig);
  }, [baseConfig, userConfig]);

  useEffect(() => {
    if (!onConfigError) return;
    if (firedRef.current) return;

    if (hasObjectKeys(userConfig) && isEmptyConfig(baseConfig)) {
      firedRef.current = true;
      onConfigError("Not supported");
    }
  }, [userConfig, baseConfig, onConfigError]);

  const setUserConfig = (config: unknown) => {
    setUserConfigState(config);
  };

  const value = useMemo<FeatureFlagsContextValue>(() => {
    return {
      config: mergedConfig,
      setUserConfig,
    };
  }, [mergedConfig]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlagsConfig() {
  const ctx = useContext(FeatureFlagsContext);
  if (ctx === undefined) {
    throw new Error(
      "useFeatureFlagsConfig must be used within FeatureFlagsProvider",
    );
  }

  return ctx.config;
}

export function useSetUserConfig() {
  const ctx = useContext(FeatureFlagsContext);
  if (ctx === undefined) {
    throw new Error(
      "useSetUserConfig must be used within FeatureFlagsProvider",
    );
  }

  return ctx.setUserConfig;
}
